import React, { useState } from 'react'
import { useEffect } from 'react'
import { useCookies } from 'react-cookie'
import { useAuth } from '../hooks/useAuth'
import { apiUrl } from '../Services/api'
import Base from '../Components/Base'
import {
  Grid,
  Box,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  Chip,
  Avatar,
  Switch,
  FormControlLabel,
  Divider,
} from '@mui/material'
import { useThemeMode } from '../Providers/ThemeModeProvider'
import DarkModeIcon from '@mui/icons-material/DarkMode'
import LightModeIcon from '@mui/icons-material/LightMode'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'

function Profile() {
  const { deleteTokenCookie } = useAuth()
  const [cookies] = useCookies(['token'])
  const [profile, setProfile] = useState(null)
  const [isEditable, setIsEditable] = useState(false)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)
  const { mode, toggleTheme } = useThemeMode()

  // Suppression des valeurs vide par null
  const sanitizePayload = (payload) => {
    const sanitizedPayload = {}
    for (const key in payload) {
      sanitizedPayload[key] = payload[key] === '' ? null : payload[key]
    }
    return sanitizedPayload
  }

  // Fonction pour récupérer le profil
  const fetchProfile = () => {
    const xhr = new XMLHttpRequest()
    xhr.open('GET', apiUrl('/users/get/my-profile'), true)

    xhr.setRequestHeader('Authorization', 'Bearer ' + cookies.token)

    xhr.onload = () => {
      if (xhr.status === 401) {
        deleteTokenCookie()
        return
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(xhr.responseText)
          setProfile(data)
        } catch (e) {
          setError('Erreur lors du traitement des données.')
        }
      } else {
        setError(
          `Erreur ${xhr.status}: Impossible de récupérer les informations de profil.`
        )
      }
      setLoading(false)
    }

    xhr.onerror = () => {
      setError('Une erreur réseau est survenue.')
      setLoading(false)
    }

    xhr.send()
  }

  // Fonction pour récupérer le profil
  const updateProfile = () => {
    const xhr = new XMLHttpRequest()
    xhr.open('PATCH', apiUrl('/users/update/my-profile'), true)
    xhr.setRequestHeader('Content-Type', 'application/json')
    xhr.setRequestHeader('Authorization', 'Bearer ' + cookies.token)

    xhr.onload = () => {
      if (xhr.status === 401) {
        deleteTokenCookie()
        return
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(xhr.responseText)
          setProfile(data)
        } catch (e) {
          setError('Erreur lors du traitement des données.')
        }
      } else {
        setError(
          `Erreur ${xhr.status}: Profil non mis à jour, une erreur est survenue.`
        )
      }
      setLoading(false)
    }

    xhr.onerror = () => {
      setError('Une erreur réseau est survenue.')
      setLoading(false)
    }
    // const sanitizedPayload = sanitizePayload(payload)
    xhr.send(JSON.stringify(sanitizePayload(profile)))
  }

  // Charger le profil au montage
  useEffect(() => {
    fetchProfile()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Fonction pour gérer les modifications des champs
  const handleFieldChange = (field, value) => {
    setProfile((prevProfile) => ({
      ...prevProfile,
      [field]: value,
    }))
  }

  // Fonction pour basculer le mode édition
  const toggleEditMode = () => {
    if (isEditable) {
      setLoading(true)
      updateProfile()
    }
    setIsEditable((prev) => !prev)
  }

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <Alert severity="error">{error}</Alert>
      </Box>
    )
  }

  return (
    <div>
      <Base>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h4" fontWeight="600">Mon profil</Typography>
          <Chip
            onClick={toggleEditMode}
            label={isEditable ? 'Sauvegarder' : 'Modifier'}
            color={isEditable ? "primary" : "default"}
            sx={{
              fontSize: '1rem', // Agrandir le texte
              padding: '5px', // Ajouter de l'espace interne
              height: '40px', // Ajuster la hauteur
              borderRadius: '8px',
            }}
          />
        </Box>
        
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          padding={2}
          marginTop={4}
        >
          <Box display="flex" justifyContent="center" marginBottom={4}>
            <Avatar
              sx={{
                bgcolor: 'primary.main',
                width: 100,
                height: 100,
                fontSize: '3rem',
                boxShadow: 2,
              }}
            >
              {profile.nickname ? profile.nickname[0].toUpperCase() : 'U'}
            </Avatar>
          </Box>
          <Grid container spacing={3} maxWidth="md">
            {profile && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Nom d'utilisateur"
                    value={profile.nickname || ''}
                    onChange={(e) =>
                      handleFieldChange('nickname', e.target.value)
                    }
                    variant="outlined"
                    fullWidth
                    InputProps={{
                      readOnly: !isEditable,
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Adresse e-mail"
                    value={profile.email || ''}
                    onChange={(e) => handleFieldChange('email', e.target.value)}
                    variant="outlined"
                    fullWidth
                    type="email"
                    InputProps={{
                      readOnly: !isEditable,
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Prénom"
                    value={profile.prenom || ''}
                    onChange={(e) =>
                      handleFieldChange('prenom', e.target.value)
                    }
                    variant="outlined"
                    fullWidth
                    InputProps={{
                      readOnly: !isEditable,
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    label="Date de naissance"
                    value={profile.date_de_naissance ? dayjs(profile.date_de_naissance) : null}
                    onChange={(val) => handleFieldChange('date_de_naissance', val ? val.format('YYYY-MM-DD') : '')}
                    disabled={!isEditable}
                    slotProps={{ textField: { fullWidth: true, variant: 'outlined', InputLabelProps: { shrink: true } }, field: { clearable: true } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Objectif annuel de lecture"
                    value={profile.annual_goal || ''}
                    onChange={(e) => handleFieldChange('annual_goal', e.target.value)}
                    variant="outlined"
                    fullWidth
                    type="number"
                    InputProps={{
                      readOnly: !isEditable,
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
              </>
            )}
          </Grid>
          
          <Divider sx={{ width: '100%', my: 6, maxWidth: 'md' }} />

          <Box width="100%" maxWidth="md">
            <Typography variant="h6" fontWeight="600" gutterBottom>Préférences d'Affichage</Typography>
            <Box display="flex" alignItems="center" gap={2} mt={2}>
              <LightModeIcon color={mode === 'light' ? 'primary' : 'disabled'} />
              <FormControlLabel
                control={<Switch checked={mode === 'dark'} onChange={toggleTheme} />}
                label={mode === 'dark' ? "Mode Sombre" : "Mode Clair"}
              />
              <DarkModeIcon color={mode === 'dark' ? 'primary' : 'disabled'} />
            </Box>
          </Box>
        </Box>
      </Base>
    </div>
  )
}

export default Profile
